import { useState, useEffect } from "react";
import { storyService } from "../../services/storyService";
import { resolveImageUrl } from "../../utils/imageUrl";
import { AdminLayout, Lightbox } from "../organisms";

export const StoriesManagement = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { images: string[], index: number } | null
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    imageUrl: "",
    uploadedFile: null,
    fileName: "",
    isPublished: true,
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Fetch stories on component mount
  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      setLoading(true);
      const data = await storyService.getAllStories();
      setStories(data);
    } catch (error) {
      console.error("Error fetching stories:", error);
      setError("Failed to load stories");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Only image files are allowed");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("Image must be 10 MB or smaller");
      return;
    }

    setError("");
    try {
      const { imageUrl } = await storyService.uploadImage(file);
      setFormData((prev) => ({
        ...prev,
        uploadedFile: imageUrl,
        fileName: file.name,
        imageUrl,
      }));
    } catch (err) {
      setError(err.message || "Failed to upload image");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!formData.title || !formData.description || !formData.imageUrl) {
      setError("Title, description, and image URL are required");
      return;
    }

    try {
      const storyData = {
        title: formData.title,
        description: formData.description,
        imageUrl: formData.imageUrl,
        isPublished: formData.isPublished,
      };

      if (editingId) {
        await storyService.updateStory(editingId, storyData);
        setSuccess("Story updated successfully!");
      } else {
        await storyService.createStory(
          storyData.title,
          storyData.description,
          storyData.imageUrl,
          storyData.isPublished,
        );
        setSuccess("Story created successfully!");
      }

      setFormData({
        title: "",
        description: "",
        imageUrl: "",
        uploadedFile: null,
        fileName: "",
        isPublished: true,
      });
      setEditingId(null);
      setShowForm(false);
      fetchStories();
    } catch (error) {
      console.error("Error saving story:", error);
      setError("Failed to save story");
    }
  };

  const handleEdit = (story) => {
    setFormData({
      title: story.title,
      description: story.description,
      imageUrl: story.imageUrl,
      uploadedFile: null,
      fileName: "",
      isPublished: story.isPublished,
    });
    setEditingId(story.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this story?")) {
      try {
        await storyService.deleteStory(id);
        setSuccess("Story deleted successfully!");
        fetchStories();
      } catch (error) {
        console.error("Error deleting story:", error);
        setError("Failed to delete story");
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      title: "",
      description: "",
      imageUrl: "",
      uploadedFile: null,
      fileName: "",
      isPublished: true,
    });
    setError("");
  };

  if (loading) {
    return (
      <AdminLayout title="Stories Management">
        <p className="text-ink-soft">Loading stories...</p>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Stories Management">
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-2xl font-bold text-ink">All Stories</h2>
        <button
          onClick={() => {
            if (showForm && !editingId) {
              handleCancel();
            } else {
              setShowForm(!showForm);
              setEditingId(null);
            }
          }}
          className={`px-3 py-1.5 text-sm rounded-xl font-semibold transition-colors ${
            showForm && !editingId
              ? "bg-ink-soft text-white hover:bg-ink"
              : "bg-ink-teal text-white hover:bg-ink-teal/90"
          }`}
        >
          {showForm && !editingId ? "Cancel" : "Add New Story"}
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
          {success}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="mb-8 bg-white p-6 rounded-2xl shadow-soft border border-terracotta-100/60">
          <h2 className="text-xl font-bold mb-6">
            {editingId ? "Edit Story" : "Create New Story"}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-1">Title</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-ink-teal"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-ink-teal"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2">
                Story Image
              </label>
              <div className="space-y-4">
                {/* File Upload */}
                <div>
                  <label
                    htmlFor="imageUpload"
                    className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-ink-teal/40 rounded-xl bg-stone-50 hover:bg-stone-100 cursor-pointer transition-colors"
                  >
                    <div className="text-center">
                      <svg
                        className="mx-auto h-10 w-10 text-ink-teal mb-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <p className="text-sm text-ink-teal font-semibold">
                        {formData.fileName || "Click to upload image"}
                      </p>
                    </div>
                    <input
                      id="imageUpload"
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                </div>

                {/* Or URL Input */}
                <div>
                  <label className="text-xs text-ink-soft block mb-2">
                    OR paste image URL:
                  </label>
                  {/* type="text" not "url": uploaded images are stored as
                        relative /uploads/ paths, which fail type="url" HTML5
                        validation and silently block form submission. The
                        backend validates the value (http(s) or /uploads/). */}
                  <input
                    type="text"
                    name="imageUrl"
                    value={formData.uploadedFile ? "" : formData.imageUrl}
                    onChange={handleInputChange}
                    placeholder="https://example.com/image.jpg or /uploads/..."
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-ink-teal text-sm"
                    disabled={!!formData.uploadedFile}
                  />
                </div>

                {/* Preview */}
                {formData.imageUrl && (
                  <div className="mt-3 p-3 bg-cream rounded-lg">
                    <p className="text-xs text-ink-soft font-semibold mb-2">
                      Preview:
                    </p>
                    <img
                      src={resolveImageUrl(formData.imageUrl)}
                      alt="Preview"
                      className="max-w-xs h-auto rounded-lg border border-terracotta-200 cursor-pointer"
                      onClick={() =>
                        setLightbox({ images: [formData.imageUrl], index: 0 })
                      }
                      onError={(e) => {
                        e.target.src =
                          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Crect fill='%23ddd' width='200' height='200'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial'%3EImage not available%3C/text%3E%3C/svg%3E";
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                name="isPublished"
                checked={formData.isPublished}
                onChange={handleInputChange}
                className="h-4 w-4 text-ink-teal rounded"
                id="isPublished"
              />
              <label htmlFor="isPublished" className="ml-2 text-sm">
                Published
              </label>
            </div>
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                className="px-3 py-1.5 text-sm bg-ink-teal text-white rounded-xl hover:bg-ink-teal/90 font-semibold"
              >
                {editingId ? "Update Story" : "Create Story"}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="px-3 py-1.5 text-sm bg-stone-200 text-ink rounded-xl hover:bg-stone-300 font-semibold"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Stories List */}
      <div className="grid gap-6">
        {stories.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-terracotta-100/60">
            <p className="text-ink-soft">No stories yet. Create one!</p>
          </div>
        ) : (
          stories.map((story) => (
            <div
              key={story.id}
              className="bg-white rounded-2xl shadow-soft border border-terracotta-100/60 overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="p-6">
                <div className="flex gap-6">
                  {story.imageUrl && (
                    <img
                      src={resolveImageUrl(story.imageUrl)}
                      alt={story.title}
                      className="w-32 h-32 object-cover rounded-lg flex-shrink-0 cursor-pointer"
                      onClick={() =>
                        setLightbox({ images: [story.imageUrl], index: 0 })
                      }
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  )}
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-xl font-bold text-ink">
                        {story.title}
                      </h3>
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          story.isPublished
                            ? "bg-green-100 text-green-800"
                            : "bg-cream text-ink-soft"
                        }`}
                      >
                        {story.isPublished ? "Published" : "Draft"}
                      </span>
                    </div>
                    <p className="text-ink-soft mb-4 line-clamp-2">
                      {story.description}
                    </p>
                    <p className="text-xs text-ink-soft mb-4">
                      Created: {new Date(story.createdAt).toLocaleDateString()}
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleEdit(story)}
                        className="px-3 py-1.5 text-sm bg-ink-teal text-white rounded-xl hover:bg-ink-teal/90 font-semibold"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(story.id)}
                        className="px-3 py-1.5 text-sm bg-terracotta-600 text-white rounded-xl hover:bg-terracotta-700 font-semibold"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      <Lightbox
        images={lightbox?.images ?? []}
        startIndex={lightbox?.index ?? 0}
        isOpen={lightbox !== null}
        onClose={() => setLightbox(null)}
        alt={() => "Story image"}
      />
    </AdminLayout>
  );
};
